/**
 * Workout logging screen (client). Ported verbatim from the original app's
 * workouts/log/page.tsx — same UI, with next/navigation → react-router and the
 * api client. Reads GET /api/workouts/programs, POST /api/workouts/logs.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Dumbbell,
  Plus,
  Minus,
  ArrowLeftRight,
  SkipForward,
  Undo2,
  Check,
  Timer,
  X,
  PlayCircle,
} from 'lucide-react';
import { ExercisePicker } from '@/components/ExercisePicker';
import { api } from '@/lib/api';

interface SetData {
  setNumber: number;
  weight: string;
  reps: string;
  rpe: string;
  isWarmup: boolean;
  completed: boolean;
}

interface ExerciseData {
  exerciseId: string;
  exerciseName: string;
  muscleGroup: string;
  videoUrl?: string | null;
  sets: SetData[];
  skipped: boolean;
  restSeconds: number;
}

interface ProgramsResponse {
  programs?: Array<{
    workouts?: Array<{
      id: string;
      workout_exercises?: Array<{
        sort_order: number;
        rest_seconds: number;
        exercises: { id: string; name: string; muscle_group: string; video_url?: string | null };
        workout_sets?: Array<{ set_number: number; target_weight: number; target_reps: number; is_warmup: boolean }>;
      }>;
    }>;
  }>;
}

export default function WorkoutLog() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [exercises, setExercises] = useState<ExerciseData[]>([]);
  const [showRestTimer, setShowRestTimer] = useState(false);
  const [restSeconds, setRestSeconds] = useState(90);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState<{ type: 'add' } | { type: 'substitute'; index: number }>({ type: 'add' });
  const startTimeRef = useRef(new Date());

  // Load workout exercises from program data
  useEffect(() => {
    async function load() {
      const workoutId = params.get('workout');
      if (!workoutId) {
        // Custom workout — start empty
        return;
      }
      try {
        const data = await api.get<ProgramsResponse>('/api/workouts/programs');
        for (const p of data.programs || []) {
          const workout = p.workouts?.find((w) => w.id === workoutId);
          if (workout) {
            setExercises(
              workout.workout_exercises
                ?.sort((a, b) => a.sort_order - b.sort_order)
                .map((we) => ({
                  exerciseId: we.exercises.id,
                  exerciseName: we.exercises.name,
                  muscleGroup: we.exercises.muscle_group || '',
                  videoUrl: we.exercises.video_url ?? null,
                  skipped: false,
                  restSeconds: we.rest_seconds || 90,
                  sets: we.workout_sets
                    ?.sort((a, b) => a.set_number - b.set_number)
                    .map((s) => ({
                      setNumber: s.set_number,
                      weight: s.target_weight ? String(s.target_weight) : '',
                      reps: s.target_reps ? String(s.target_reps) : '',
                      rpe: '',
                      isWarmup: s.is_warmup,
                      completed: false,
                    })) || [{ setNumber: 1, weight: '', reps: '', rpe: '', isWarmup: false, completed: false }],
                })) || []
            );
            break;
          }
        }
      } catch { /* */ }
    }
    load();
  }, [params]);

  // Rest timer countdown
  useEffect(() => {
    if (!showRestTimer || remainingSeconds <= 0) return;
    const t = setTimeout(() => setRemainingSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [showRestTimer, remainingSeconds]);

  useEffect(() => {
    if (remainingSeconds === 0 && showRestTimer) setShowRestTimer(false);
  }, [remainingSeconds, showRestTimer]);

  function completeSet(exIndex: number, setIndex: number) {
    setExercises((prev) => {
      const next = [...prev];
      next[exIndex] = { ...next[exIndex], sets: [...next[exIndex].sets] };
      next[exIndex].sets[setIndex] = { ...next[exIndex].sets[setIndex], completed: true };
      return next;
    });
    setRestSeconds(exercises[exIndex].restSeconds || 90);
    setRemainingSeconds(exercises[exIndex].restSeconds || 90);
    setShowRestTimer(true);
  }

  function updateSet(exIndex: number, setIndex: number, field: 'weight' | 'reps' | 'rpe', value: string) {
    setExercises((prev) => {
      const next = [...prev];
      next[exIndex] = { ...next[exIndex], sets: [...next[exIndex].sets] };
      next[exIndex].sets[setIndex] = { ...next[exIndex].sets[setIndex], [field]: value };
      return next;
    });
  }

  function addSet(exIndex: number) {
    setExercises((prev) => {
      const next = [...prev];
      const sets = [...next[exIndex].sets];
      sets.push({ setNumber: sets.length + 1, weight: '', reps: '', rpe: '', isWarmup: false, completed: false });
      next[exIndex] = { ...next[exIndex], sets };
      return next;
    });
  }

  function removeSet(exIndex: number) {
    setExercises((prev) => {
      const next = [...prev];
      const sets = [...next[exIndex].sets];
      if (sets.length > 1) sets.pop();
      next[exIndex] = { ...next[exIndex], sets };
      return next;
    });
  }

  function toggleSkip(exIndex: number) {
    setExercises((prev) => {
      const next = [...prev];
      next[exIndex] = { ...next[exIndex], skipped: !next[exIndex].skipped };
      return next;
    });
  }

  const completedSets = exercises.reduce((sum, ex) => sum + (ex.skipped ? 0 : ex.sets.filter((s) => s.completed).length), 0);
  const totalSets = exercises.reduce((sum, ex) => sum + (ex.skipped ? 0 : ex.sets.length), 0);

  async function handleFinish() {
    setSaving(true);
    const durationMinutes = Math.round((Date.now() - startTimeRef.current.getTime()) / 60000);
    try {
      await api.post('/api/workouts/logs', {
        workout_id: params.get('workout'),
        program_id: params.get('program'),
        workout_type: 'strength',
        duration_minutes: durationMinutes,
        exercises: exercises
          .filter((ex) => !ex.skipped)
          .map((ex) => ({
            exercise_id: ex.exerciseId,
            sets: ex.sets
              .filter((s) => s.completed)
              .map((s) => ({
                set_number: s.setNumber,
                actual_reps: s.reps ? parseInt(s.reps) : null,
                actual_weight: s.weight ? parseFloat(s.weight) : null,
                rpe: s.rpe ? parseInt(s.rpe) : null,
                is_warmup: s.isWarmup,
              })),
          })),
      });
      setSaved(true);
    } catch { /* */ }
    finally { setSaving(false); }
  }

  if (saved) {
    return (
      <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Check size={64} style={{ color: 'var(--success)' }} />
        <h1 className="display mt-4" style={{ fontSize: "2.1rem" }}>Workout Saved!</h1>
        <p className="text-sm mt-2" style={{ color: 'var(--gray-400)' }}>{completedSets} sets completed</p>
        <button onClick={() => navigate('/workouts')} className="btn-gold w-full mt-6">Done</button>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Fixed Header */}
      <div className="sticky top-0 z-20 px-5 pt-[max(16px,env(safe-area-inset-top))] pb-3" style={{ background: 'var(--navy-dark)' }}>
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} style={{ color: 'var(--gold)' }}>
            <X size={22} />
          </button>
          <p className="text-sm font-medium text-white">{completedSets}/{totalSets} sets</p>
          <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'var(--surface)', color: 'var(--gray-400)' }}>
            <Timer size={10} className="inline mr-1" />
            {Math.round((Date.now() - startTimeRef.current.getTime()) / 60000)} min
          </span>
        </div>
      </div>

      {/* Exercises */}
      <div className="px-5 flex flex-col gap-5">
        {exercises.map((ex, exIndex) => (
          <div key={exIndex} className="card" style={{ opacity: ex.skipped ? 0.5 : 1 }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Dumbbell size={16} style={{ color: 'var(--gold)' }} />
                <div>
                  <p className="text-sm font-semibold text-white">{ex.exerciseName}</p>
                  <div className="flex items-center gap-2">
                    {ex.muscleGroup && <p className="text-xs" style={{ color: 'var(--gray-500)' }}>{ex.muscleGroup}</p>}
                    {ex.videoUrl && (
                      <a
                        href={ex.videoUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs"
                        style={{ color: 'var(--gold)' }}
                      >
                        <PlayCircle size={12} /> Watch demo
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setPickerMode({ type: 'substitute', index: exIndex }); setShowPicker(true); }} className="p-1.5 rounded" style={{ color: 'var(--gray-400)' }}>
                  <ArrowLeftRight size={14} />
                </button>
                <button onClick={() => toggleSkip(exIndex)} className="p-1.5 rounded" style={{ color: ex.skipped ? 'var(--gold)' : 'var(--gray-400)' }}>
                  {ex.skipped ? <Undo2 size={14} /> : <SkipForward size={14} />}
                </button>
              </div>
            </div>

            {ex.skipped ? (
              <p className="text-xs text-center py-2" style={{ color: 'var(--warning)' }}>Skipped</p>
            ) : (
              <>
                {/* Set Headers */}
                <div className="grid grid-cols-[32px_1fr_1fr_48px_32px] gap-2 text-xs font-medium mb-1" style={{ color: 'var(--gray-500)' }}>
                  <span>SET</span><span>LBS</span><span>REPS</span><span>RPE</span><span></span>
                </div>

                {/* Sets */}
                {ex.sets.map((set, setIndex) => (
                  <div key={setIndex} className="grid grid-cols-[32px_1fr_1fr_48px_32px] gap-2 items-center mb-1.5">
                    <span className="text-xs text-center" style={{ color: set.isWarmup ? 'var(--warning)' : 'var(--gray-400)' }}>
                      {set.isWarmup ? 'W' : set.setNumber}
                    </span>
                    <input
                      type="number"
                      className="input text-center text-sm py-1.5 px-1"
                      placeholder="0"
                      value={set.weight}
                      onChange={(e) => updateSet(exIndex, setIndex, 'weight', e.target.value)}
                      disabled={set.completed}
                    />
                    <input
                      type="number"
                      className="input text-center text-sm py-1.5 px-1"
                      placeholder="0"
                      value={set.reps}
                      onChange={(e) => updateSet(exIndex, setIndex, 'reps', e.target.value)}
                      disabled={set.completed}
                    />
                    <input
                      type="number"
                      className="input text-center text-sm py-1.5 px-1"
                      placeholder="-"
                      value={set.rpe}
                      onChange={(e) => updateSet(exIndex, setIndex, 'rpe', e.target.value)}
                      disabled={set.completed}
                    />
                    <button
                      onClick={() => !set.completed && completeSet(exIndex, setIndex)}
                      className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
                      style={{
                        background: set.completed ? 'var(--success)' : 'transparent',
                        border: set.completed ? 'none' : '1.5px solid var(--gray-600)',
                        color: set.completed ? 'white' : 'var(--gray-600)',
                      }}
                    >
                      {set.completed && <Check size={14} strokeWidth={2.5} />}
                    </button>
                  </div>
                ))}

                {/* Add/Remove Set */}
                <div className="flex gap-2 mt-2">
                  <button onClick={() => addSet(exIndex)} className="flex items-center gap-1 text-xs py-1.5 px-3 rounded-lg border" style={{ borderColor: 'var(--gold-muted)', color: 'var(--gold)' }}>
                    <Plus size={12} /> Add Set
                  </button>
                  {ex.sets.length > 1 && (
                    <button onClick={() => removeSet(exIndex)} className="flex items-center gap-1 text-xs py-1.5 px-3 rounded-lg border" style={{ borderColor: 'var(--navy-light)', color: 'var(--gray-400)' }}>
                      <Minus size={12} /> Remove
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}

        <button
          onClick={() => { setPickerMode({ type: 'add' }); setShowPicker(true); }}
          className="btn-outline w-full flex items-center justify-center gap-2 mb-3"
        >
          <Plus size={16} /> Add Exercise
        </button>

        <button onClick={handleFinish} className="btn-gold w-full" disabled={completedSets === 0 || saving}>
          {saving ? 'Saving...' : `Finish Workout (${completedSets} sets)`}
        </button>

        <ExercisePicker
          title={pickerMode.type === 'substitute' ? 'Substitute Exercise' : 'Add Exercise'}
          isOpen={showPicker}
          onClose={() => setShowPicker(false)}
          onSelect={(ex) => {
            if (pickerMode.type === 'substitute') {
              setExercises((prev) => {
                const next = [...prev];
                next[pickerMode.index] = { ...next[pickerMode.index], exerciseId: ex.id, exerciseName: ex.name, videoUrl: ex.video_url ?? null };
                return next;
              });
            } else {
              setExercises((prev) => [...prev, {
                exerciseId: ex.id,
                exerciseName: ex.name,
                muscleGroup: ex.muscle_group || '',
                videoUrl: ex.video_url ?? null,
                sets: [{ setNumber: 1, weight: '', reps: '', rpe: '', isWarmup: false, completed: false }],
                skipped: false,
                restSeconds: 90,
              }]);
            }
          }}
        />
      </div>

      {/* Rest Timer Overlay */}
      {showRestTimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div className="text-center">
            <p className="text-xs uppercase tracking-wider mb-4" style={{ color: 'var(--gold-muted)' }}>Rest Timer</p>
            <p className="text-6xl font-bold text-white mb-2">
              {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
            </p>
            <div className="w-48 h-1.5 rounded-full mx-auto mb-6" style={{ background: 'var(--navy-light)' }}>
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${(remainingSeconds / restSeconds) * 100}%`, background: 'var(--gold)' }}
              />
            </div>
            <button onClick={() => setShowRestTimer(false)} className="btn-outline text-sm">
              Skip Rest
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
